import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";

// Component Imports
import { PlayerPool } from "./PlayerPool";
import { DraftBoard } from "./DraftBoard";
import { LiveRosters } from "./LiveRosters";
import { DraftOrder } from "./DraftOrder";

// Renamed to Admin to match your Admin.tsx filename
export const Admin = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [showSingle, setShowSingle] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [newPlayer, setNewPlayer] = useState({
    name: "",
    position: "",
    hometown: "",
  });
  const [bulkList, setBulkList] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");

  const fetchSessions = async () => {
    const { data } = await supabase
      .from("draft_sessions")
      .select("*")
      .order("year", { ascending: false });

    if (data) {
      setSessions(data);
      if (currentSession) {
        const found = data.find((s: any) => s.id === currentSession.id);
        if (found) setCurrentSession(found);
      }
    }
  };

  useEffect(() => {
    fetchSessions();

    // Setting global styles for the admin aesthetic
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.backgroundColor = "#050505";
    return () => {
      document.body.style.margin = "";
    };
  }, []);

  useEffect(() => {
    if (currentSession?.scheduled_start) {
      const date = new Date(currentSession.scheduled_start);
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      const hours = ("0" + date.getHours()).slice(-2);
      const minutes = ("0" + date.getMinutes()).slice(-2);
      setScheduledTime(`${year}-${month}-${day}T${hours}:${minutes}`);
    }
  }, [currentSession]);

  const handleUpdateStatus = async (status: string) => {
    if (!currentSession) return;
    const isLive = status === "live";
    await supabase
      .from("draft_sessions")
      .update({
        status,
        is_active: isLive,
        is_paused: !isLive,
      })
      .eq("id", currentSession.id);
    fetchSessions();
  };

  const createNewSession = async () => {
    const yearInput = window.prompt("ENTER DRAFT YEAR:");
    if (!yearInput) return;
    await supabase.from("draft_sessions").insert([
      {
        year: parseInt(yearInput),
        status: "pre-draft",
        is_active: false,
        is_paused: true,
      },
    ]);
    fetchSessions();
  };

  const updateSessionYear = async () => {
    if (!currentSession) return;
    const newYear = window.prompt("ENTER NEW YEAR:", currentSession.year);
    if (!newYear) return;

    await supabase
      .from("draft_sessions")
      .update({ is_current: false })
      .neq("id", "00000000-0000-0000-0000-000000000000");

    await supabase
      .from("draft_sessions")
      .update({
        year: parseInt(newYear),
        is_current: true,
      })
      .eq("id", currentSession.id);

    fetchSessions();
  };

  const deleteSession = async () => {
    if (
      currentSession &&
      window.confirm(`DANGER: DELETE ALL DATA FOR ${currentSession.year}?`)
    ) {
      await supabase.from("picks").delete().eq("session_id", currentSession.id);
      await supabase
        .from("players")
        .delete()
        .eq("session_id", currentSession.id);
      await supabase
        .from("draft_sessions")
        .delete()
        .eq("id", currentSession.id);
      setCurrentSession(null);
      fetchSessions();
    }
  };

  const updateCountdownTime = async () => {
    if (!currentSession || !scheduledTime) return;
    await supabase
      .from("draft_sessions")
      .update({ scheduled_start: new Date(scheduledTime).toISOString() })
      .eq("id", currentSession.id);
    fetchSessions();
  };

  const undoLastPick = async () => {
    const { data: lastPick } = await supabase
      .from("picks")
      .select("id, player_id")
      .eq("session_id", currentSession.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (lastPick && window.confirm("Undo last pick?")) {
      await supabase
        .from("players")
        .update({ is_drafted: false, team_id: null })
        .eq("id", lastPick.player_id);

      await supabase.from("picks").delete().eq("id", lastPick.id);
      window.location.reload();
    }
  };

  const addSinglePlayer = async () => {
    if (!newPlayer.name || !currentSession) return;
    await supabase
      .from("players")
      .insert([
        { ...newPlayer, is_drafted: false, session_id: currentSession.id },
      ]);
    setNewPlayer({ name: "", position: "", hometown: "" });
    setShowSingle(false);
    fetchSessions();
  };

  const bulkUpload = async () => {
    if (!currentSession) return;
    const players = bulkList
      .split("\n")
      .filter((l) => l.trim())
      .map((line) => {
        const [n, p, h] = line.split(",").map((i) => i?.trim());
        return {
          name: n || "Unknown",
          position: p || "",
          hometown: h || "",
          is_drafted: false,
          session_id: currentSession.id,
        };
      });
    await supabase.from("players").insert(players);
    setBulkList("");
    setShowBulk(false);
    fetchSessions();
  };

  const inputStyle: React.CSSProperties = {
    background: "rgba(0, 0, 0, 0.8)",
    color: "#f9eeda",
    border: "1px solid #22565a",
    padding: "10px",
    fontFamily: "'Oswald', sans-serif",
    textTransform: "uppercase",
  };

  const btnStyle: React.CSSProperties = {
    background: "#22565a",
    color: "#f9eeda",
    border: "none",
    padding: "10px 20px",
    cursor: "pointer",
    fontFamily: "'Oswald', sans-serif",
    fontWeight: "bold",
    textTransform: "uppercase",
  };

  return (
    <div
      style={{
        backgroundColor: "#050505",
        background:
          "radial-gradient(circle at center, #1b4d52 0%, #050505 100%)",
        minHeight: "100vh",
        fontFamily: "'Oswald', sans-serif",
      }}
    >
      <style>
        {`
          @media (max-width: 768px) {
            header { padding: 15px !important; flex-direction: column !important; text-align: center; gap: 15px; }
            header h1 { font-size: 1.5rem !important; }
            main { padding: 15px !important; }
            .session-bar { flex-direction: column !important; gap: 8px !important; }
            .date-picker-bar { flex-direction: column !important; gap: 10px !important; text-align: center; }
            .single-add-bar { flex-direction: column !important; }
            .mobile-full { width: 100% !important; box-sizing: border-box; }
            .draft-content { overflow-x: auto; }
          }
        `}
      </style>

      <header
        style={{
          padding: "20px 50px",
          borderBottom: "4px solid #22565a",
          backgroundColor: "rgba(8, 8, 8, 0.9)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <img
          src="https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png"
          alt="Logo"
          style={{ height: "50px" }}
        />
        <h1
          style={{
            color: "#f9eeda",
            margin: 0,
            fontSize: "2.2rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "2px",
          }}
        >
          EMPIRE COMMAND CENTER
        </h1>
        <button
          onClick={() => supabase.auth.signOut()}
          className="mobile-full"
          style={{
            ...btnStyle,
            background: "transparent",
            border: "1px solid #ff4444",
            color: "#ff4444",
          }}
        >
          LOGOUT
        </button>
      </header>

      <main style={{ padding: "30px 50px" }}>
        {/* Session Management Bar */}
        <div
          className="session-bar"
          style={{
            background: "rgba(0,0,0,0.85)",
            padding: "20px",
            border: "1px solid #22565a",
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            marginBottom: "30px",
          }}
        >
          <button
            onClick={createNewSession}
            className="mobile-full"
            style={btnStyle}
          >
            + New Session
          </button>
          <select
            value={currentSession?.id || ""}
            onChange={(e) =>
              setCurrentSession(sessions.find((s) => s.id === e.target.value))
            }
            className="mobile-full"
            style={inputStyle}
          >
            <option value="">SELECT SESSION</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.year} {s.status.toUpperCase()}
              </option>
            ))}
          </select>
          <button
            onClick={updateSessionYear}
            className="mobile-full"
            style={btnStyle}
          >
            Set Year
          </button>
          <button
            onClick={() => handleUpdateStatus("pre-draft")}
            className="mobile-full"
            style={btnStyle}
          >
            Pre-Draft
          </button>
          <button
            onClick={undoLastPick}
            className="mobile-full"
            style={btnStyle}
          >
            Undo Pick
          </button>
          <button
            onClick={deleteSession}
            className="mobile-full"
            style={{ ...btnStyle, background: "#4a1212" }}
          >
            Delete Session
          </button>

          {currentSession &&
            (currentSession.status !== "live" ? (
              <button
                onClick={() => handleUpdateStatus("live")}
                className="mobile-full"
                style={{
                  ...btnStyle,
                  background: "#1a4a1a",
                  border: "1px solid #4ade80",
                }}
              >
                START DRAFT
              </button>
            ) : (
              <button
                onClick={() => handleUpdateStatus("concluded")}
                className="mobile-full"
                style={{
                  ...btnStyle,
                  background: "#4a1212",
                  border: "1px solid #ef4444",
                }}
              >
                END DRAFT
              </button>
            ))}
        </div>

        {currentSession && (
          <>
            <div
              className="date-picker-bar"
              style={{
                background: "rgba(0,0,0,0.85)",
                padding: "20px",
                border: "1px solid #22565a",
                marginBottom: "30px",
                display: "flex",
                gap: "15px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ color: "#f9eeda", letterSpacing: "2px" }}>
                Draft Date & Time:
              </span>
              <input
                type="datetime-local"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="mobile-full"
                style={inputStyle}
              />
              <button
                onClick={updateCountdownTime}
                className="mobile-full"
                style={btnStyle}
              >
                Update Countdown
              </button>
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                justifyContent: "center",
                marginBottom: "30px",
              }}
            >
              <button
                onClick={() => setShowSingle(!showSingle)}
                className="mobile-full"
                style={btnStyle}
              >
                Single Add
              </button>
              <button
                onClick={() => setShowBulk(!showBulk)}
                className="mobile-full"
                style={btnStyle}
              >
                Bulk Upload
              </button>
            </div>

            {showSingle && (
              <div
                className="single-add-bar"
                style={{
                  background: "rgba(0,0,0,0.85)",
                  padding: "20px",
                  border: "1px solid #22565a",
                  marginBottom: "20px",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center",
                }}
              >
                <input
                  placeholder="NAME"
                  value={newPlayer.name}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, name: e.target.value })
                  }
                  className="mobile-full"
                  style={inputStyle}
                />
                <input
                  placeholder="POSITION"
                  value={newPlayer.position}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, position: e.target.value })
                  }
                  className="mobile-full"
                  style={inputStyle}
                />
                <input
                  placeholder="HOMETOWN"
                  value={newPlayer.hometown}
                  onChange={(e) =>
                    setNewPlayer({ ...newPlayer, hometown: e.target.value })
                  }
                  className="mobile-full"
                  style={inputStyle}
                />
                <button
                  onClick={addSinglePlayer}
                  className="mobile-full"
                  style={btnStyle}
                >
                  Save Player
                </button>
              </div>
            )}

            {showBulk && (
              <div
                style={{
                  background: "rgba(0,0,0,0.85)",
                  padding: "20px",
                  border: "1px solid #22565a",
                  marginBottom: "20px",
                }}
              >
                <textarea
                  placeholder="NAME, POSITION, HOMETOWN"
                  rows={6}
                  value={bulkList}
                  onChange={(e) => setBulkList(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "100%",
                    marginBottom: "10px",
                    boxSizing: "border-box",
                  }}
                />
                <button
                  onClick={bulkUpload}
                  className="mobile-full"
                  style={btnStyle}
                >
                  UPLOAD LIST
                </button>
              </div>
            )}

            <div
              className="draft-content"
              style={{
                maxWidth: "1400px",
                margin: "0 auto",
                borderTop: "2px solid #22565a",
                paddingTop: "40px",
              }}
            >
              <DraftBoard sessionId={currentSession.id} />
              <DraftOrder sessionId={currentSession.id} />
              <PlayerPool
                isAdmin={true}
                sessionId={currentSession.id}
                canDraft={currentSession?.status === "live"}
              />
              <LiveRosters isAdmin={true} sessionId={currentSession.id} />
            </div>
          </>
        )}
      </main>
    </div>
  );
};
